/**
 * /admin/blocks — Phase 3 admin dashboard for the auto block generator.
 *
 * Surfaces:
 *  • KPI strip: Available / Claimed / In progress / Completed
 *  • Available-by-sector strip (mirror of marketplace sector counts)
 *  • Manual "Generate blocks" trigger (returns structured summary)
 *  • Filters: status, source, sector
 *  • Row list with status badge, source badge, sector, time window,
 *    stops, pay, driver, and admin actions (force release, cancel).
 *
 * Data feed:
 *   GET  /api/admin/blocks/summary
 *   GET  /api/admin/blocks?status=&source=&sector_id=
 *   POST /api/admin/blocks/generate
 *   POST /api/admin/blocks/{id}/force-release
 *   POST /api/admin/blocks/{id}/cancel
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, RefreshCw, PlayCircle, Unlock, XCircle, Filter, Layers, Sparkles, Users, Wifi, WifiOff, Activity } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiError } from "@/shared/lib/api";

const STATUS_BADGE = {
  available: "bg-emerald-100 text-emerald-700 border-emerald-200",
  claimed: "bg-orange-100 text-orange-700 border-orange-200",
  in_progress: "bg-blue-100 text-blue-700 border-blue-200",
  completed: "bg-slate-100 text-slate-600 border-slate-200",
  cancelled: "bg-red-100 text-red-700 border-red-200",
};
const SOURCE_BADGE = {
  auto: "bg-[#0A1128] text-white",
  admin: "bg-[#ed6500] text-white",
  demo: "bg-[#F1ECE3] text-[#515E7B] border border-[#0A1128]/10",
  seed_phase1: "bg-[#F1ECE3] text-[#515E7B] border border-[#0A1128]/10",
};

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function AdminBlocks() {
  const [summary, setSummary] = useState(null);
  const [network, setNetwork] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [lastGen, setLastGen] = useState(null);

  const [statusFilter, setStatusFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [sectorFilter, setSectorFilter] = useState("");

  // Driver visibility diagnostic (ops tool — answers "why does this
  // driver see nothing in the marketplace?")
  const [diagEmail, setDiagEmail] = useState("");
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagResult, setDiagResult] = useState(null);
  const runDiagnostic = useCallback(async () => {
    const email = (diagEmail || "").trim().toLowerCase();
    if (!email) return;
    setDiagLoading(true);
    setDiagResult(null);
    try {
      const r = await api.get(`/admin/diagnostics/driver-visibility?email=${encodeURIComponent(email)}`);
      setDiagResult(r.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Diagnostic failed");
    } finally {
      setDiagLoading(false);
    }
  }, [diagEmail]);

  const loadSummary = useCallback(async () => {
    try {
      const [s, n] = await Promise.all([
        api.get("/admin/blocks/summary"),
        api.get("/admin/driver-network"),
      ]);
      setSummary(s.data);
      setNetwork(n.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not load summary");
    }
  }, []);

  const loadBlocks = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const params = { limit: 200 };
      if (statusFilter) params.status = statusFilter;
      if (sourceFilter) params.source = sourceFilter;
      if (sectorFilter) params.sector_id = sectorFilter;
      const { data } = await api.get("/admin/blocks", { params });
      setBlocks(data.blocks || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not load blocks");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [statusFilter, sourceFilter, sectorFilter]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadBlocks(); }, [loadBlocks]);

  const onGenerate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/blocks/generate", {});
      setLastGen(data);
      toast.success(`Generated ${data.blocks_created} block(s) from ${data.trips_scanned} trip(s)`);
      await Promise.all([loadSummary(), loadBlocks(true)]);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const onForceRelease = async (id) => {
    if (!window.confirm("Force-release this block back to the marketplace? Stop statuses will reset.")) return;
    try {
      await api.post(`/admin/blocks/${id}/force-release`);
      toast.success("Block released");
      await Promise.all([loadSummary(), loadBlocks(true)]);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Release failed");
    }
  };

  const onCancel = async (id) => {
    if (!window.confirm("Cancel this block? Its trips will be freed for re-generation.")) return;
    try {
      await api.post(`/admin/blocks/${id}/cancel`);
      toast.success("Block cancelled");
      await Promise.all([loadSummary(), loadBlocks(true)]);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Cancel failed");
    }
  };

  const sectors = useMemo(() => summary?.available_by_sector || [], [summary]);
  const counts = summary?.counts || { available: 0, claimed: 0, in_progress: 0, completed: 0, cancelled: 0, total: 0 };
  const bySource = summary?.by_source || {};

  return (
    <>
      <div className="max-w-6xl mx-auto px-3 sm:px-6 py-6" data-testid="admin-blocks-page">
        {/* Header */}
        <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#515E7B] mb-1">Phase 3 · Auto Block Generator</div>
            <h1 className="font-serif text-[28px] sm:text-[36px] tracking-tight text-[#0A1128]">Driver Blocks</h1>
            <p className="text-[13px] text-[#515E7B] mt-1">
              Auto-generated from approved-rider trips · seeded fallback when demand is light.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => loadBlocks(true)}
              disabled={refreshing}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-[12px] text-[#515E7B] hover:text-[#0A1128] border border-[#0A1128]/10 rounded-full bg-white"
              data-testid="admin-blocks-refresh"
            >
              {refreshing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Refresh
            </button>
            <button
              onClick={onGenerate}
              disabled={generating}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-[12px] font-semibold text-white bg-[#ed6500] hover:bg-[#ff7b1f] rounded-full disabled:opacity-60"
              data-testid="admin-blocks-generate"
            >
              {generating ? <Loader2 size={14} className="animate-spin" /> : <PlayCircle size={14} />} Generate now
            </button>
          </div>
        </div>

        {/* Driver network metrics */}
        {network && (
          <div className="bg-white border border-[#0A1128]/10 rounded-2xl p-4 mb-4" data-testid="admin-driver-network">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#515E7B] mb-3">
              <Activity size={12} /> Driver network
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
              <NetworkCell icon={<Users size={14} />}   label="Total drivers" value={network.drivers.total}     testid="admin-net-total" />
              <NetworkCell icon={<Wifi size={14} className="text-emerald-500" />}   label="Online"  value={network.drivers.online}  testid="admin-net-online" />
              <NetworkCell icon={<WifiOff size={14} className="text-[#515E7B]" />}  label="Offline" value={network.drivers.offline} testid="admin-net-offline" />
              <NetworkCell icon={<Activity size={14} className="text-[#ed6500]" />} label="On block" value={network.drivers.on_block} testid="admin-net-on-block" />
              <NetworkCell icon={<Layers size={14} />}  label="Available blocks"   value={network.blocks.available}   testid="admin-net-avail" />
              <NetworkCell icon={<Layers size={14} />}  label="In progress"        value={network.blocks.claimed + network.blocks.in_progress} testid="admin-net-active" />
            </div>
          </div>
        )}

        {/* KPI strip */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4" data-testid="admin-blocks-kpis">
          {[
            { k: "available", label: "Available", v: counts.available },
            { k: "claimed", label: "Claimed", v: counts.claimed },
            { k: "in_progress", label: "In progress", v: counts.in_progress },
            { k: "completed", label: "Completed", v: counts.completed },
            { k: "cancelled", label: "Cancelled", v: counts.cancelled },
          ].map(({ k, label, v }) => (
            <div key={k} className="bg-white border border-[#0A1128]/10 rounded-2xl p-4" data-testid={`admin-blocks-kpi-${k}`}>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#515E7B] mb-1">{label}</div>
              <div className="font-serif text-[28px] text-[#0A1128]">{v}</div>
            </div>
          ))}
        </div>

        {/* Source mix */}
        <div className="bg-white border border-[#0A1128]/10 rounded-2xl p-4 mb-4">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#515E7B] mb-2">
            <Layers size={12} /> Inventory mix
          </div>
          <div className="flex flex-wrap gap-2" data-testid="admin-blocks-source-mix">
            {Object.entries(bySource).map(([src, n]) => (
              <span
                key={src}
                className={`px-3 py-1 rounded-full text-[11px] tracking-wide ${SOURCE_BADGE[src] || "bg-slate-100 text-slate-600"}`}
                data-testid={`admin-blocks-source-${src}`}
              >
                {src} · {n}
              </span>
            ))}
            {Object.keys(bySource).length === 0 && (
              <span className="text-[12px] text-[#515E7B]">No blocks yet — try generating from current demand.</span>
            )}
          </div>
        </div>

        {/* Driver visibility diagnostic — "Why doesn't this driver see blocks?" */}
        <div className="bg-white border border-[#0A1128]/10 rounded-2xl p-4 mb-4" data-testid="admin-blocks-diagnostic">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#515E7B] mb-2">
            <Users size={12} /> Driver visibility check
          </div>
          <p className="text-[11px] text-[#515E7B] mb-3">
            Enter a driver email to see exactly what their marketplace feed returns and the root-cause diagnosis.
          </p>
          <div className="flex flex-wrap gap-2 items-center">
            <input
              type="email"
              placeholder="driver@email.com"
              value={diagEmail}
              onChange={(e) => setDiagEmail(e.target.value)}
              className="flex-1 min-w-[200px] h-10 px-3 rounded-full border border-[#0A1128]/15 text-[13px] text-[#0A1128] placeholder-[#9AA3B7] focus:outline-none focus:border-[#ed6500]"
              data-testid="admin-diag-email"
              onKeyDown={(e) => { if (e.key === "Enter") runDiagnostic(); }}
            />
            <button
              type="button"
              onClick={runDiagnostic}
              disabled={diagLoading || !diagEmail.trim()}
              className="h-10 px-5 rounded-full bg-[#0A1128] text-white text-[11px] tracking-[0.18em] uppercase font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid="admin-diag-check"
            >
              {diagLoading ? <Loader2 size={14} className="animate-spin" /> : <Activity size={14} />} Check visibility
            </button>
          </div>
          {diagResult && (
            <div className="mt-4 space-y-3" data-testid="admin-diag-result">
              <div className={`rounded-2xl p-3 text-[12.5px] ${diagResult.will_pass_driver_gate && diagResult.visible_to_driver.total > 0 ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-amber-50 text-amber-900 border border-amber-200"}`}>
                <div className="font-semibold uppercase tracking-[0.16em] text-[10px] mb-1">Diagnosis</div>
                {diagResult.diagnosis}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11.5px]">
                <DiagCell label="User exists" value={diagResult.gates.user_exists ? "Yes" : "No"} good={diagResult.gates.user_exists} />
                <DiagCell label="is_driver" value={diagResult.gates.is_driver ? "true" : "false"} good={diagResult.gates.is_driver} />
                <DiagCell label="is_active" value={diagResult.gates.is_active ? "true" : "false"} good={diagResult.gates.is_active} />
                <DiagCell label="access_status" value={diagResult.gates.access_status || "—"} good={!diagResult.gates.access_status || diagResult.gates.access_status === "APPROVED"} />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-[11.5px]">
                <DiagCell label="Blocks visible to this driver (14d)" value={diagResult.visible_to_driver.total} good={diagResult.visible_to_driver.total > 0} />
                <DiagCell label="Window" value={`${diagResult.window.from} → ${diagResult.window.to}`} good />
                <DiagCell label="Will pass driver gate" value={diagResult.will_pass_driver_gate ? "Yes" : "No"} good={diagResult.will_pass_driver_gate} />
              </div>
              {diagResult.inventory_breakdown?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#515E7B] mb-1">Inventory in next 14 days</div>
                  <div className="flex flex-wrap gap-1.5">
                    {diagResult.inventory_breakdown.map((r, i) => (
                      <span key={i} className="px-2 py-1 rounded-full bg-[#F1ECE3] text-[#0A1128] text-[10.5px]">
                        {r.source || "—"} · {r.status} · {r.count}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {diagResult.visible_to_driver.sample?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#515E7B] mb-1">Sample visible blocks</div>
                  <div className="bg-[#F9F7F4] rounded-xl border border-[#0A1128]/[0.05] divide-y divide-[#0A1128]/[0.05]">
                    {diagResult.visible_to_driver.sample.map((b) => (
                      <div key={b.id} className="px-3 py-2 flex items-center justify-between text-[11.5px]" data-testid={`admin-diag-sample-${b.id}`}>
                        <span className="text-[#0A1128]">{b.date} · {b.window_start}–{b.window_end} · {b.sector_name}</span>
                        <span className="text-[#515E7B]">${b.pay_dollars} · {b.legs_count} leg{b.legs_count === 1 ? "" : "s"} · {b.source}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sector availability strip (matches marketplace count markers) */}
        <div className="bg-white border border-[#0A1128]/10 rounded-2xl p-4 mb-4" data-testid="admin-blocks-sector-strip">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#515E7B] mb-3">
            <Sparkles size={12} /> Available by sector
          </div>
          <div className="flex flex-wrap gap-2">
            {sectors.map((s) => (
              <button
                key={s.sector_id}
                onClick={() => setSectorFilter(sectorFilter === s.sector_id ? "" : s.sector_id)}
                className={`px-3 py-1.5 rounded-full text-[12px] border transition-colors ${
                  sectorFilter === s.sector_id
                    ? "bg-[#0A1128] text-white border-[#0A1128]"
                    : "bg-white text-[#0A1128] border-[#0A1128]/10 hover:border-[#0A1128]/30"
                }`}
                data-testid={`admin-blocks-sector-chip-${s.sector_id}`}
              >
                {s.sector_name} <span className="opacity-60">· {s.count}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Last generation result */}
        {lastGen && (
          <div className="bg-[#0A1128] text-white rounded-2xl p-4 mb-4 text-[12px]" data-testid="admin-blocks-last-gen">
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/60 mb-2">Last generation</div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <Stat label="Scanned" v={lastGen.trips_scanned} />
              <Stat label="Assigned" v={lastGen.trips_assigned} />
              <Stat label="Created" v={lastGen.blocks_created} />
              <Stat label="Out of area" v={lastGen.out_of_area} />
              <Stat label="Low demand" v={lastGen.low_demand_buckets} />
            </div>
            {Object.keys(lastGen.by_sector || {}).length > 0 && (
              <div className="mt-3 text-[11px] text-white/70">
                Created in: {Object.entries(lastGen.by_sector).map(([k, v]) => `${k} (${v})`).join(", ")}
              </div>
            )}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white border border-[#0A1128]/10 rounded-2xl p-3 mb-4 flex flex-wrap items-center gap-2" data-testid="admin-blocks-filters">
          <Filter size={14} className="text-[#515E7B]" />
          <FilterSelect label="Status" value={statusFilter} onChange={setStatusFilter} testid="admin-blocks-filter-status" options={[
            { v: "", t: "All" }, { v: "available", t: "Available" }, { v: "claimed", t: "Claimed" },
            { v: "in_progress", t: "In progress" }, { v: "completed", t: "Completed" }, { v: "cancelled", t: "Cancelled" },
          ]} />
          <FilterSelect label="Source" value={sourceFilter} onChange={setSourceFilter} testid="admin-blocks-filter-source" options={[
            { v: "", t: "All" }, { v: "auto", t: "Auto" }, { v: "admin", t: "Admin" }, { v: "demo", t: "Demo" },
          ]} />
          {sectorFilter && (
            <button
              onClick={() => setSectorFilter("")}
              className="px-3 py-1.5 rounded-full text-[11px] border border-[#0A1128]/10 text-[#515E7B]"
              data-testid="admin-blocks-clear-sector"
            >Clear sector</button>
          )}
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center py-16 text-[#515E7B]"><Loader2 className="animate-spin inline mr-2" size={16} /> Loading blocks…</div>
        ) : blocks.length === 0 ? (
          <div className="text-center py-16 text-[#515E7B]" data-testid="admin-blocks-empty">No blocks match the current filters.</div>
        ) : (
          <div className="space-y-2" data-testid="admin-blocks-list">
            {blocks.map((b) => (
              <div key={b.id} className="bg-white border border-[#0A1128]/10 rounded-2xl p-4" data-testid={`admin-block-row-${b.id}`}>
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide border ${STATUS_BADGE[b.status] || ""}`}
                        data-testid={`admin-block-status-${b.id}`}>{b.status.replace("_", " ")}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide ${SOURCE_BADGE[b.source] || "bg-slate-100 text-slate-600"}`}>
                    {b.source}
                  </span>
                  <span className="text-[12px] text-[#0A1128] font-medium">{b.sector_name}</span>
                  <span className="text-[12px] text-[#515E7B]">· {b.date} · {b.window_start}–{b.window_end}</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-[12px]">
                  <Cell label="Pay" v={`$${b.pay_dollars.toFixed(2)}`} />
                  <Cell label="Stops" v={`${b.stops_count} (${b.pickup_count} pu · ${b.dropoff_count} do)`} />
                  <Cell label="ETA" v={`${Math.round(b.estimated_minutes / 60 * 10) / 10}h`} />
                  <Cell label="Driver" v={b.driver_email || "—"} />
                  <Cell label="Created" v={formatDate(b.created_at)} />
                </div>
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  <code className="text-[10px] text-[#515E7B] truncate">{b.id}</code>
                  {(b.status === "claimed" || b.status === "in_progress") && (
                    <button
                      onClick={() => onForceRelease(b.id)}
                      className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 text-[11px] text-[#0A1128] border border-[#0A1128]/10 rounded-full hover:bg-[#F9F7F4]"
                      data-testid={`admin-block-release-${b.id}`}
                    >
                      <Unlock size={12} /> Force release
                    </button>
                  )}
                  {b.status !== "cancelled" && b.status !== "completed" && (
                    <button
                      onClick={() => onCancel(b.id)}
                      className={`${b.status === "claimed" || b.status === "in_progress" ? "" : "ml-auto"} inline-flex items-center gap-1 px-3 py-1.5 text-[11px] text-red-700 border border-red-200 rounded-full hover:bg-red-50`}
                      data-testid={`admin-block-cancel-${b.id}`}
                    >
                      <XCircle size={12} /> Cancel
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function Stat({ label, v }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">{label}</div>
      <div className="font-serif text-[22px]">{v}</div>
    </div>
  );
}

function NetworkCell({ icon, label, value, testid }) {
  return (
    <div className="rounded-xl bg-[#F9F7F4] border border-black/[0.05] p-3" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#515E7B] mb-1">{icon}{label}</div>
      <div className="font-serif text-[22px] text-[#0A1128]">{value}</div>
    </div>
  );
}

function Cell({ label, v }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-[0.2em] text-[#515E7B]">{label}</div>
      <div className="text-[12px] text-[#0A1128]">{v}</div>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] uppercase tracking-[0.2em] text-[#515E7B]">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-white border border-[#0A1128]/10 rounded-full px-3 py-1.5 text-[12px] text-[#0A1128]"
        data-testid={testid}
      >
        {options.map((o) => <option key={o.v} value={o.v}>{o.t}</option>)}
      </select>
    </div>
  );
}


function DiagCell({ label, value, good }) {
  return (
    <div className={`rounded-xl border p-2.5 ${good ? "bg-emerald-50/70 border-emerald-200" : "bg-red-50/70 border-red-200"}`}>
      <div className="text-[9.5px] uppercase tracking-[0.18em] text-[#515E7B]">{label}</div>
      <div className="text-[12px] text-[#0A1128] font-semibold mt-0.5 break-words">{String(value)}</div>
    </div>
  );
}
